// The agency console (ADR-0015).
//
// What an agency with ten clients actually logs into: every client down the
// page, who is on each, and one place to take somebody off all of them.
//
// The screen is arranged around the two operations that used to be impossible
// rather than merely tedious. Putting a person on a client was ten separate
// invitations only the clients could send. Taking a person off was ten clients
// each remembering — and the two who forgot left an ex-employee with publishing
// rights on a Facebook Page. So "Offboard" says how many clients it touched,
// and every client card shows its own end date, because an engagement that has
// quietly expired looks exactly like one that has not until someone tries to
// publish.

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useWorkspace } from "../lib/WorkspaceContext";
import { listRoles, type Role } from "../lib/people";
import { ROLE_LABEL } from "../lib/workspace";
import {
  agencyPortfolio, agencyStaff, assignAgencyMember, removeOrganisationMember,
  revokeEngagement, unassignAgencyMember,
  type AgencyStaff, type Engagement,
} from "../lib/agency";
// ADR-0021: the agency is the organisation this workspace belongs to, not the
// workspace itself. The handle, the roster and the seats all live there.
import {
  myOrganisations, organisationSeats, setOrganisationHandle,
  type Organisation, type Seats,
} from "../lib/organisation";
import { logError } from "../lib/logger";

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

const isPast = (iso: string | null) => !!iso && new Date(iso).getTime() <= Date.now();

export function Agency() {
  const { active, can, refresh } = useWorkspace();
  const workspaceId = active?.workspace_id ?? null;

  const [org, setOrg] = useState<Organisation | null>(null);
  const [seats, setSeats] = useState<Seats | null>(null);
  const handle = org?.handle ?? null;
  const [handleDraft, setHandleDraft] = useState("");
  const [engagements, setEngagements] = useState<Engagement[]>([]);
  const [staff, setStaff] = useState<AgencyStaff[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Access this offboarding deliberately left standing. Held separately from
  // `done` because it is a warning, not a receipt.
  const [retained, setRetained] = useState<
    { client_name: string; roles: string[]; reason: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    // The organisation this workspace sits in is the agency. A workspace with no
    // organisation has no clients — that is not an error, it is the ordinary
    // case for everybody who is not an agency.
    const mine = await myOrganisations();
    const ours = mine.find((o) => o.organisation_id === active?.organisation_id)
              ?? mine.find((o) => o.kind === "agency")
              ?? null;
    setOrg(ours);
    setRoles(await listRoles());
    if (!ours) {
      setEngagements([]);
      setStaff([]);
      setSeats(null);
      return;
    }
    setEngagements(await agencyPortfolio(ours.organisation_id));
    setStaff(await agencyStaff(ours.organisation_id));
    setSeats(await organisationSeats(ours.organisation_id).catch(() => null));
  }, [workspaceId, active?.organisation_id]);

  useEffect(() => {
    load().catch((e) => {
      setError(e.message ?? String(e));
      void logError("app", e, { step: "load_agency", workspaceId });
    });
  }, [load, workspaceId]);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      await fn();
      await load();
      await refresh();
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  const mayManage = can("member.manage");
  const mayManageWorkspace = can("workspace.manage");

  if (!can("member.view")) {
    return (
      <div className="people-page">
        <div className="card muted">You do not have permission to see this workspace's clients.</div>
      </div>
    );
  }

  const live = engagements.filter((e) => e.status === "active");
  const ended = engagements.filter((e) => e.status !== "active");

  return (
    <div className="people-page">
      <header className="people-head">
        <div>
          <h1>Clients</h1>
          <p className="muted">
            Every business that has authorised {active?.name ?? "this agency"}, who you have on it,
            and when the authorisation ends. Clients decide what you may do; you decide who does it.
          </p>
        </div>
        <span className="shield-pill">
          You cannot widen what a client granted. Only they can.
        </span>
      </header>

      {error && <div className="card err">{error}</div>}
      {done && <div className="card notice ok-note">{done}</div>}

      {/* The half an offboarding cannot do. A grant the client made directly is
          theirs, not the agency's, so it survives — and the agency has to be
          told, or "we offboarded them" is false in a way nobody discovers. */}
      {retained.length > 0 && (
        <div className="card err">
          <strong>Still has access at {retained.length === 1
            ? "1 client" : `${retained.length} clients`}.</strong>
          <p className="muted card-sub">
            These were granted by the client directly, not through your engagement, so ending
            your engagement does not end them. Ask the client to remove them.
          </p>
          <ul className="can-list">
            {retained.map((r) => (
              <li className="cannot" key={r.client_name}>
                <strong>{r.client_name}</strong>
                {r.roles?.length > 0 && ` — ${r.roles.map((c) => ROLE_LABEL[c] ?? c).join(", ")}`}
              </li>
            ))}
          </ul>
          <button className="sm" onClick={() => setRetained([])}>Dismiss</button>
        </div>
      )}

      {!org && (
        <div className="card notice">
          <strong>This workspace is not part of an organisation yet.</strong>
          <p className="muted card-sub" style={{ marginBottom: 0 }}>
            An agency is an organisation (ADR-0021): the handle clients type, the staff roster and
            the seats all belong to it, so that two workspaces do not mean two rosters. Create one
            to start taking clients.
          </p>
        </div>
      )}

      {/* Seats are earned by connections, not bought one at a time — so the
          number that matters before hiring is how many are left, and where the
          next one comes from. */}
      {seats && (
        <div className="card">
          <div className="card-head">
            <h2>Seats</h2>
            <span className={seats.used >= seats.allowed ? "chip warn" : "chip"}>
              {seats.used} of {seats.allowed} used
            </span>
          </div>
          <p className="muted card-sub" style={{ marginBottom: 0 }}>
            {seats.base} included on your plan
            {seats.from_connections > 0 && <>
              , {seats.from_connections} earned by the {seats.connections} account
              {seats.connections === 1 ? "" : "s"} you manage
            </>}
            {seats.purchased > 0 && <>, {seats.purchased} bought</>}
            . One further seat for every {seats.ratio} accounts under management.
          </p>
        </div>
      )}

      {/* Without a handle no client can find this agency, so it is the first
          thing the page asks for rather than a setting buried elsewhere. */}
      {org && <div className="card">
        <div className="card-head">
          <h2>Your handle</h2>
          {handle && <span className="chip ok">@{handle}</span>}
        </div>
        <p className="muted card-sub">
          The one identifier a client types to authorise you — said once, by you, instead of typed
          differently by each of them. Send it to a client and they enter it under{" "}
          <strong>People</strong> in their own workspace.
        </p>
        {mayManageWorkspace && (
          <div className="row">
            <input
              type="text"
              value={handleDraft || handle || ""}
              placeholder="bluepeak"
              aria-label="Agency handle"
              style={{ maxWidth: "16rem" }}
              onChange={(e) => setHandleDraft(e.target.value.toLowerCase())}
            />
            <button
              className="sm"
              disabled={busy || !(handleDraft || handle)}
              onClick={() => run(async () => {
                await setOrganisationHandle(
                  org!.organisation_id, handleDraft || handle || "");
                setHandleDraft("");
              })}
            >
              {handle ? "Change handle" : "Claim handle"}
            </button>
          </div>
        )}
        {!handle && !mayManageWorkspace && (
          <p className="muted">No handle yet. Ask an owner to claim one.</p>
        )}
      </div>}

      <div className="people-grid left-heavy">
        <div className="people-col">
          <div className="card">
            <div className="card-head">
              <h2>Clients</h2>
              <span className="muted">
                {live.length === 1 ? "1 active" : `${live.length} active`}
                {ended.length > 0 && ` · ${ended.length} ended`}
              </span>
            </div>

            {live.length === 0 && (
              <p className="muted card-sub" style={{ marginBottom: 0 }}>
                No client has authorised you yet. Send them your handle
                {handle ? <> — <strong>@{handle}</strong></> : null}; they enter it under People in
                their workspace and choose what you may do.
              </p>
            )}

            {live.map((e) => (
              <ClientRow
                key={e.id}
                engagement={e}
                staff={staff}
                roles={roles}
                busy={busy}
                mayManage={mayManage}
                onRun={run}
              />
            ))}

            {ended.length > 0 && (
              <details className="shape-detail">
                <summary>{ended.length} ended engagement{ended.length > 1 ? "s" : ""}</summary>
                <ul className="shape-list">
                  {ended.map((e) => (
                    <li key={e.id}>
                      <strong>{e.client_name}</strong>
                      <span className="muted"> · ended</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </div>

        <div className="people-col">
          <div className="card">
            <div className="card-head">
              <h2>Your team</h2>
              <span className="muted">
                {staff.length === 1 ? "1 person" : `${staff.length} people`}
              </span>
            </div>
            <p className="muted card-sub">
              Invite people here under <Link to="/people">People</Link>. Being in this workspace
              gives them nothing at a client until you put them on one.
            </p>

            {staff.map((p) => (
              <div className="person" key={p.user_id}>
                <div className="person-main simple">
                  <div className="person-body">
                    <div className="person-head">
                      <strong>{p.email ?? p.user_id}</strong>
                      {p.is_owner && <span className="chip ok">Owner</span>}
                    </div>
                    <div className="person-roles">
                      <span className="muted">
                        {p.clients === 0
                          ? "Not on any client"
                          : p.clients === 1 ? "On 1 client" : `On ${p.clients} clients`}
                      </span>
                    </div>
                  </div>
                  {mayManage && !p.is_owner && (
                    <button
                      className="sm danger"
                      disabled={busy}
                      onClick={() => {
                        if (!confirm(
                          `Offboard ${p.email ?? "this person"}?\n\n`
                          + `Their access ends immediately at ${p.clients === 1 ? "the 1 client" : `all ${p.clients} clients`} `
                          + "they are on, and they leave this workspace. It happens in one "
                          + "transaction — there is no half-removed state.")) return;
                        void run(async () => {
                          const result = await removeOrganisationMember({
                            organisationId: org!.organisation_id, userId: p.user_id,
                          });
                          // What it did, not that it finished. "Removed from 7
                          // clients" is the confirmation; "done" is not.
                          //
                          // And what it did NOT do. A client who invited this
                          // person directly holds a grant that is not ours to
                          // revoke — it survives, and saying "removed" over the
                          // top of it is the failure this list exists to
                          // prevent (ADR-0021 L-C).
                          setDone(
                            `${p.email ?? "That person"} was removed from `
                            + `${result.clients_affected} client${result.clients_affected === 1 ? "" : "s"} `
                            + `(${result.assignments_removed} assignment${result.assignments_removed === 1 ? "" : "s"}) `
                            + "and has left this workspace.");
                          setRetained(result.retained ?? []);
                        });
                      }}
                    >
                      Offboard
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ClientRow({
  engagement: e, staff, roles, busy, mayManage, onRun,
}: {
  engagement: Engagement;
  staff: AgencyStaff[];
  roles: Role[];
  busy: boolean;
  mayManage: boolean;
  onRun: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  const [adding, setAdding] = useState(false);
  const [who, setWho] = useState("");
  const [role, setRole] = useState(e.allowed_roles[0] ?? "creator");
  const [account, setAccount] = useState(e.accounts[0]?.id ?? "");

  const expired = isPast(e.expires_at);
  const onIt = new Set(e.people.map((p) => p.user_id));
  const available = staff.filter((s) => !onIt.has(s.user_id));

  return (
    <section className="outside-group">
      {/* Name and status on one line, the envelope on the next. Inline they
          wrapped, and a status chip that lands under the client's name reads
          as belonging to the row below it. */}
      <header className="client-head">
        <strong>{e.client_name}</strong>
        {expired
          ? <span className="chip err">Expired</span>
          : e.expires_at
            ? <span className="chip">Until {shortDate(e.expires_at)}</span>
            : <span className="chip warn">No end date</span>}
      </header>
      <p className="muted client-meta">
        {e.scope === "workspace"
          ? "Whole workspace"
          : e.accounts.length === 1
            ? e.accounts[0].name
            : `${e.accounts.length} accounts`}
        {" · may act as "}
        {e.allowed_roles.map((r) => ROLE_LABEL[r] ?? r).join(" or ")}
      </p>

      {/* The client set this. Saying so is the difference between a limit that
          reads as ours and one that reads as theirs. */}
      {expired && (
        <p className="notice card-sub">
          This authorisation has run out, so nothing here can publish. Ask {e.client_name} to
          renew it — you cannot extend it yourself.
        </p>
      )}

      <div className="person-roles" style={{ margin: ".2rem 0 .5rem" }}>
        {e.people.length === 0 && <span className="muted">Nobody assigned yet.</span>}
        {e.people.map((p) => (
          <span key={`${p.user_id}-${p.role}-${p.scope_id ?? "ws"}`} className="chip role-chip">
            {p.email ?? p.user_id}
            <span className="muted"> · {ROLE_LABEL[p.role] ?? p.role}</span>
            {p.expires_at && <span className="muted"> · until {shortDate(p.expires_at)}</span>}
            {mayManage && (
              <button className="link chip-x" title="Take them off this client" disabled={busy}
                      onClick={() => onRun(() => unassignAgencyMember(e.id, p.user_id))}>×</button>
            )}
          </span>
        ))}
      </div>

      {mayManage && (
        <div className="row person-actions" style={{ marginLeft: 0 }}>
          {!adding && (
            <button className="sm" disabled={busy || available.length === 0}
                    onClick={() => { setAdding(true); setWho(available[0]?.user_id ?? ""); }}>
              {available.length === 0 ? "Everyone is on this client" : "Put someone on this client"}
            </button>
          )}

          {adding && (
            <>
              <select value={who} disabled={busy} aria-label="Who"
                      onChange={(ev) => setWho(ev.target.value)}>
                {available.map((s) => (
                  <option key={s.user_id} value={s.user_id}>{s.email ?? s.user_id}</option>
                ))}
              </select>

              <select value={role} disabled={busy} aria-label="Role"
                      onChange={(ev) => setRole(ev.target.value)}>
                {/* Only what the client allowed. The server refuses the rest. */}
                {e.allowed_roles.map((r) => (
                  <option key={r} value={r}>
                    {roles.find((x) => x.code === r)?.name ?? ROLE_LABEL[r] ?? r}
                  </option>
                ))}
              </select>

              {e.scope === "connections" && (
                <select value={account} disabled={busy} aria-label="Account"
                        onChange={(ev) => setAccount(ev.target.value)}>
                  {e.accounts.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              )}

              <button
                className="sm primary"
                disabled={busy || !who || (e.scope === "connections" && !account)}
                onClick={() => onRun(async () => {
                  await assignAgencyMember({
                    engagementId: e.id, userId: who, role,
                    connectionId: e.scope === "connections" ? account : null,
                  });
                  setAdding(false);
                })}
              >
                Add
              </button>
              <button className="sm" disabled={busy} onClick={() => setAdding(false)}>Cancel</button>
            </>
          )}

          <button
            className="sm danger"
            disabled={busy}
            onClick={() => {
              if (!confirm(
                `End the engagement with ${e.client_name}?\n\n`
                + `${e.people.length} of your people lose access to their accounts immediately. `
                + "Only they can authorise you again.")) return;
              void onRun(() => revokeEngagement(e.id));
            }}
          >
            End engagement
          </button>
        </div>
      )}
    </section>
  );
}
